/**
 * MRI Chatbot Block
 *
 * Injection block that loads the MRI Engage CRM chatbot. The vendor script
 * defines the <mri-chat-widget> custom element, which paints its launcher and
 * chat iframe into its own shadow root for the bot named by `chatbot-key`.
 *
 * Why singleton + never-tear-down: the element reads its attributes only in
 * its constructor and registers window listeners and timers it never cleans
 * up, and the script declares a top-level class, so loading it twice throws.
 * So we add one widget and one script per tab and leave them alone, which
 * survives StrictMode and SPA navigation remounts. A changed chatbot key won't
 * reflect until a hard refresh, an acceptable trade for not duplicating the
 * widget. This mirrors the entrata-chatbot block.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const WIDGET_ID = 'mri-chatbot-widget';
const SCRIPT_ID = 'mri-chatbot-script';
const SCRIPT_SRC = 'https://chatbot.engagecrm.cloud.mrisoftware.com/bots/web-components/display-widget.js';

function MriChatbotComponent(_props: BlockProps) {
  const chatbotKey = useBlockToken('chatbot_key', { defaultField: 'custom_mri_chatbot' })?.trim();

  useEffect(() => {
    if (!chatbotKey || document.getElementById(WIDGET_ID)) return;

    // The element must exist with its key before the script defines it. Once
    // defined, createElement runs the constructor before setAttribute.
    const widget = document.createElement('mri-chat-widget');
    widget.id = WIDGET_ID;
    widget.setAttribute('chatbot-key', chatbotKey);
    document.body.appendChild(widget);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    document.head.appendChild(script);
  }, [chatbotKey]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'MriChatbot',
  displayName: 'MRI Chatbot',
  tokens: {
    chatbot_key: {
      label: 'Chatbot Key',
      type: 'text',
      defaultField: 'custom_mri_chatbot',
      suggestedFields: ['custom_mri_chatbot'],
      description: 'The `chatbot-key` GUID from the MRI Engage CRM chatbot embed code.',
      required: true,
    },
  },
};

export const mriChatbotBlock: BlockDefinition = {
  id: 'mri-chatbot',
  name: 'MRI Chatbot',
  description: 'MRI Engage CRM chatbot. The floating launcher is the UI. Clicking it opens the chat window.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: MriChatbotComponent,
  contentSchema,
  tags: ['mri', 'engage', 'engage crm', 'chatbot', 'chat', 'widget'],
};
